import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import AdminShell from "../../components/AdminShell";
import { useAuth } from "../../contexts/AuthContext";

function actionLabel(a) {
  if (a.status === "pending") return { text: "New endorsement request — pending review", color: "var(--warning)" };
  if (a.endorsementStatus === "ready_to_claim") return { text: "Ready to claim — collect payment when documents are received", color: "var(--info)" };
  if (a.phase2Status === "ready_to_claim") return { text: "Phase 2 ready to claim — collect payment when received", color: "var(--info)" };
  return null;
}

export default function AdminDashboard() {
  const { user, role } = useAuth();
  const [stats, setStats] = useState({ total: 0, pending: 0, accepted: 0, ready: 0, aboard: 0 });
  const [recent, setRecent] = useState([]);
  const [needsAction, setNeedsAction] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "applicants"));
        let all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Filter by assignment for non-superadmins
        if (role !== "superadmin" && user) {
          const { doc, getDoc, collection: col, getDocs: gd } = await import("firebase/firestore");
          const asnap = await getDoc(doc(db, "admin_assignments", user.uid));
          if (asnap.exists() && (asnap.data().assignedJobs || []).length > 0) {
            const assignedJobIds = asnap.data().assignedJobs;
            const hiringSnap = await gd(col(db, "hirings"));
            const hiringTitles = hiringSnap.docs.filter((d) => assignedJobIds.includes(d.id)).map((d) => d.data().title);
            all = all.filter((a) => a.assignedAdminId ? a.assignedAdminId === user.uid : (a.selectedJobs || []).some((t) => hiringTitles.includes(t)));
          }
        }

        const sorted = all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setStats({
          total: all.length,
          pending: all.filter((a) => a.status === "pending").length,
          accepted: all.filter((a) => a.status === "accepted").length,
          ready: all.filter((a) => a.endorsementStatus === "ready_to_claim").length,
          aboard: all.filter((a) => a.endorsementStatus === "for_aboard").length
        });
        setRecent(sorted.slice(0, 5));
        setNeedsAction(sorted.filter((a) => actionLabel(a) !== null));
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [user, role]);

  const cards = [
    { label: "Total Applications", value: stats.total,    icon: "ti-users" },
    { label: "Pending Review",     value: stats.pending,  icon: "ti-clock" },
    { label: "Accepted",           value: stats.accepted, icon: "ti-check" },
    { label: "Ready to Claim",     value: stats.ready,    icon: "ti-package" },
    { label: "For Aboard",         value: stats.aboard,   icon: "ti-plane" }
  ];

  return (
    <AdminShell title="Dashboard Overview">
      <div className="stats-grid">
        {cards.map((c) => (
          <div key={c.label} className="stat-card">
            <div className="stat-label">{c.label}</div>
            <div className="stat-value">{loading ? "—" : c.value}</div>
          </div>
        ))}
      </div>

      {/* Needs Action Panel */}
      {needsAction.length > 0 && (
        <div className="card mb-24" style={{ borderLeft: "4px solid var(--warning)" }}>
          <div className="flex-between mb-16">
            <h3 style={{ fontSize: 16 }}>
              <i className="ti ti-bell-ringing" style={{ color: "var(--warning)", marginRight: 8 }}></i>
              Needs Action <span className="badge badge-pending" style={{ marginLeft: 8 }}>{needsAction.length}</span>
            </h3>
            <Link to="/admin/applicants" className="btn btn-outline" style={{ fontSize: 12 }}>View All</Link>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {needsAction.slice(0, 8).map((a) => {
              const action = actionLabel(a);
              return (
                <Link key={a.id} to={`/admin/applicants/${a.id}`} style={{ textDecoration: "none" }}>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", borderRadius: 8, border: "1px solid var(--ink-100)",
                    background: "var(--cream-warm)", cursor: "pointer",
                    transition: "background 0.15s"
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{a.firstName} {a.lastName}</div>
                      <div className="mono" style={{ fontSize: 11, color: "var(--ink-400)" }}>{a.applicationId}</div>
                      <div style={{ fontSize: 12, color: action?.color, marginTop: 2, fontWeight: 500 }}>
                        <i className="ti ti-alert-circle"></i> {action?.text}
                      </div>
                    </div>
                    <i className="ti ti-chevron-right" style={{ color: "var(--ink-300)" }}></i>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Applications */}
      <div className="card">
        <div className="flex-between mb-16">
          <h3 style={{ fontSize: 18 }}>Recent Applications</h3>
          <Link to="/admin/applicants" className="btn btn-outline">View All</Link>
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 32 }}><span className="spinner" style={{ borderTopColor: "var(--gold-500)" }}></span></div>
        ) : recent.length === 0 ? (
          <div className="empty-state"><i className="ti ti-inbox"></i><div>No applications yet</div></div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>ID</th><th>Name</th><th>Status</th><th>Phase</th><th></th></tr></thead>
              <tbody>
                {recent.map((a) => (
                  <tr key={a.id}>
                    <td className="mono" style={{ fontSize: 12 }}>{a.applicationId}</td>
                    <td>{a.firstName} {a.lastName}</td>
                    <td><span className={`badge badge-${a.status}`}>{a.status}</span></td>
                    <td>
                      {a.endorsementStatus === "for_aboard" && <span className="badge badge-accepted" style={{ fontSize: 10 }}><i className="ti ti-plane"></i> Aboard</span>}
                      {a.phase2Status && <span className="badge badge-processing" style={{ fontSize: 10 }}>P2: {a.phase2Status.replace(/_/g," ")}</span>}
                      {a.endorsementStatus && a.endorsementStatus !== "for_aboard" && <span className="badge badge-processing" style={{ fontSize: 10 }}>P1: {a.endorsementStatus.replace(/_/g," ")}</span>}
                      {!a.endorsementStatus && <span style={{ fontSize: 12, color: "var(--ink-300)" }}>—</span>}
                    </td>
                    <td><Link to={`/admin/applicants/${a.id}`} className="btn btn-ghost" style={{ fontSize: 12 }}><i className="ti ti-eye"></i> View</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
